import React from "react";
import {
	Box,
	Grid,
	MenuItem,
	Stack,
	styled,
	SvgIcon,
	type Theme,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/system";
import { DropdownMenu, TenantCard } from "@packsize/components";
import type { TenantMeta } from "@packsize/identity-api-sdk";
import { packsizeString } from "@packsize/utils";
import { orderBy } from "lodash";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import FmdGoodOutlined from "@mui/icons-material/FmdGoodOutlined";
import MoveUpOutlined from "@mui/icons-material/MoveUpOutlined";
import PodcastsOutlined from "@mui/icons-material/PodcastsOutlined";

const StyledIcon = styled(SvgIcon)(({ theme }) => ({
	fontSize: theme.typography.fontSize * 1.5,
}));

interface TenantIconProps {
	readonly isLiveCustomerEnvironment: boolean;
}

const TenantIcon: React.FC<TenantIconProps> = ({ isLiveCustomerEnvironment }) => {
	if (isLiveCustomerEnvironment) {
		return (
			<StyledIcon>
				<PodcastsOutlined />
			</StyledIcon>
		);
	}

	return (
		<StyledIcon>
			<FmdGoodOutlined />
		</StyledIcon>
	);
};

interface ActiveTenantProps {
	readonly isLiveCustomerEnvironment: boolean;
	readonly tenantName: string;
}

const COMPACT_TENANT_NAME_LENGTH = 10;
const MEDIUM_TENANT_NAME_LENGTH = 24;

const ActiveTenant: React.FC<ActiveTenantProps> = ({ isLiveCustomerEnvironment, tenantName }) => {
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down("md"));
	const tenantNameLength = isCompact ? COMPACT_TENANT_NAME_LENGTH : MEDIUM_TENANT_NAME_LENGTH;
	const displayedTenantName = packsizeString.truncateMiddle(tenantName, tenantNameLength);

	return (
		<Grid
			display="grid"
			gridTemplateColumns={{ xs: "minmax(0, 1fr)", lg: "auto minmax(0, 1fr)" }}
			alignItems="center"
			columnGap={{ xs: 0, lg: 2 }}
			sx={{ width: "fit-content", maxWidth: "100%", minWidth: 0 }}
		>
			<Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center" }}>
				<TenantIcon isLiveCustomerEnvironment={isLiveCustomerEnvironment} />
			</Box>

			<Stack
				direction="column"
				spacing={0.5}
				alignItems="flex-start"
				sx={{ width: "fit-content", maxWidth: "100%", minWidth: 0 }}
			>
				<Typography
					component="span"
					variant="body1"
					data-testid="active-tenant-name"
					noWrap
					sx={{
						textAlign: "left",
						textTransform: "none",
						textOverflow: "ellipsis",
						width: "fit-content",
						maxWidth: "100%",
						minWidth: 0,
					}}
				>
					{displayedTenantName}
				</Typography>
				{isLiveCustomerEnvironment === true && (
					<Typography variant="caption" data-testid="live-environment-label" lineHeight={1} noWrap>
						<FormattedMessage id="live environment" defaultMessage="live environment" />
					</Typography>
				)}
			</Stack>
		</Grid>
	);
};

const triggerSx = (theme: Theme, isLiveCustomerEnvironment: boolean) => ({
	color: theme.palette.common.white,
	flex: "0 1 auto",
	width: "fit-content",
	minWidth: "15ch",
	maxWidth: { xs: "15ch", md: "32ch" },
	overflow: "hidden",
	"& .MuiButton-endIcon": {
		marginLeft: 2,
	},
	height: 48,
	border: "1px solid",
	borderRadius: 1,

	background: isLiveCustomerEnvironment ? alpha(theme.palette.success.main, 0.2) : "transparent",
	borderColor: isLiveCustomerEnvironment ? alpha(theme.palette.success.main, 0.6) : theme.palette.common.white,
});

const singleTriggerSx = (theme: Theme, isLiveCustomerEnvironment: boolean) => ({
	...triggerSx(theme, isLiveCustomerEnvironment),

	display: "flex",
	alignItems: "center",
	px: 2,
});

export interface TenantSelectDropDownTemplateProps {
	readonly tenantName: string;
	readonly isLiveCustomerEnvironment: boolean;
	readonly tenants: TenantMeta[];
	readonly currentTenantId: string;
	readonly onTenantSelect: (tenantId: string) => void;
}

export const TenantSelectDropDownTemplate: React.FC<TenantSelectDropDownTemplateProps> = ({
	tenantName,
	isLiveCustomerEnvironment,
	tenants,
	currentTenantId,
	onTenantSelect,
}) => {
	const theme = useTheme();
	const hasMultipleTenants = tenants.length > 0;
	const orderedTenants = orderBy(
		tenants,
		[(tenant) => tenant.id === currentTenantId, (tenant) => tenant.company.toLowerCase()],
		["desc", "asc"],
	);

	if (hasMultipleTenants === false) {
		return (
			<Box data-testid="single-tenant" sx={singleTriggerSx(theme, isLiveCustomerEnvironment)}>
				<ActiveTenant isLiveCustomerEnvironment={isLiveCustomerEnvironment} tenantName={tenantName} />
			</Box>
		);
	}

	return (
		<DropdownMenu
			label={<ActiveTenant isLiveCustomerEnvironment={isLiveCustomerEnvironment} tenantName={tenantName} />}
			buttonProps={{
				sx: triggerSx(theme, isLiveCustomerEnvironment),
			}}
			popoverProps={{
				anchorOrigin: { vertical: "bottom", horizontal: "right" },
				transformOrigin: { vertical: "top", horizontal: "right" },
				slotProps: {
					paper: {
						sx: {
							mt: 0.25,
							maxHeight: "min(600px, calc(100vh - 96px))",
							overflow: "hidden",
						},
					},
				},
			}}
		>
			<Box
				data-testid="tenant-menu-content"
				sx={{
					display: "flex",
					flexDirection: "column",
					maxHeight: "min(600px, calc(100vh - 96px))",
				}}
			>
				<Box
					data-testid="tenant-list"
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						overflowY: "auto",
						p: 2,
					}}
				>
					{orderedTenants.map((tenant) => (
						<TenantCard
							key={tenant.id}
							tenant={tenant}
							tenantStatus={tenant.tenantStatus?.name ?? "Internal"}
							currentUserId={currentTenantId}
							onClick={(selectedTenant) => onTenantSelect(selectedTenant.id)}
						/>
					))}
				</Box>
				<MenuItem
					component={Link}
					to="/site-select"
					data-testid="site-select-link"
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						p: 2,
						color: "primary.main",
					}}
				>
					<StyledIcon>
						<MoveUpOutlined />
					</StyledIcon>
					<FormattedMessage id="View All My Sites" defaultMessage="View All My Sites" />
				</MenuItem>
			</Box>
		</DropdownMenu>
	);
};
